import React, { useState, useEffect } from 'react';
import { apiKeys as apiKeysApi } from '../../api/client';
import { Key, Plus, Trash2, Loader2, Copy, CheckCircle2, Shield } from 'lucide-react';

export default function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const data = await apiKeysApi.list();
      setKeys(data.apiKeys || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadKeys(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const data = await apiKeysApi.create({ name: newName || 'API Key' });
      setNewKey(data.apiKey);
      setNewName('');
      loadKeys();
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this API key?')) return;
    try {
      await apiKeysApi.revoke(keyId);
      loadKeys();
    } catch (e) { console.error(e); }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-medium tracking-widest uppercase block mb-2 text-stone-500">Access</span>
          <h1 className="font-serif text-2xl md:text-3xl text-stone-900 dark:text-white">API Keys</h1>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-4 py-1.5 text-sm bg-stone-900 text-white dark:bg-white dark:text-stone-900 flex items-center gap-1">
          <Plus className="w-4 h-4" /> Create API Key
        </button>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-stone-700 dark:text-stone-300">Keep your API keys secure. Never share them publicly or commit them to version control.</p>
      </div>

      {/* New Key Display */}
      {newKey && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <span className="font-medium text-emerald-700 dark:text-emerald-400">API key created! Copy it now - you won't see it again.</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 font-mono text-sm text-stone-900 dark:text-white">
              {newKey.key}
            </code>
            <button onClick={() => handleCopy(newKey.key)} className="px-3 py-2 border border-stone-200 text-stone-600 flex items-center gap-1 text-sm">
              {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="text-sm text-stone-500 mt-2 hover:underline">Dismiss</button>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)} />
          <div className="relative bg-white dark:bg-stone-900 w-full max-w-lg border border-stone-200 dark:border-stone-700 shadow-xl p-6">
            <h2 className="font-serif text-xl text-stone-900 dark:text-white mb-4">Create API Key</h2>
            <div>
              <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">Name</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-white" placeholder="e.g., Chrome Extension" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm border border-stone-200 text-stone-600">Cancel</button>
              <button onClick={handleCreate} disabled={creating} className="px-4 py-2 text-sm bg-stone-900 text-white flex items-center gap-1">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>
      ) : (
        <div className="space-y-3">
          {keys.length === 0 ? (
            <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-8 text-center text-stone-500">No API keys created yet.</div>
          ) : (
            keys.map((k) => (
              <div key={k.key_id} className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 p-4 hover:border-stone-400 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-stone-400" />
                    <div>
                      <p className="font-medium text-stone-900 dark:text-white">{k.name || 'API Key'}</p>
                      <code className="text-sm text-stone-500 font-mono">{k.key}</code>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 text-xs font-medium ${k.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'}`}>
                      {k.is_active ? 'Active' : 'Revoked'}
                    </span>
                    <span className="text-xs text-stone-400">{k.created_at ? new Date(k.created_at).toLocaleDateString() : ''}</span>
                    {k.is_active && (
                      <button onClick={() => handleRevoke(k.key_id)} className="p-2 hover:bg-rose-50 rounded" title="Revoke">
                        <Trash2 className="w-4 h-4 text-rose-500" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
